import PropTypes from 'prop-types';
import React, { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import useDebounce from '../../../Hooks/useDebounce';
import Select from '../../Components/Buttons/Select';
import Modal from '../../Components/Modal';
import Text from '../../Components/Text';
import TextResource from '../../Components/TextResource';
import Sublist from '../List/Sublist';

// STYLES
import style from '../../../../scss/components/form.module.scss';
import styleList from '../../../../scss/components/list.module.scss';
import Button from '../../Components/Buttons/Button';
import { MessageContext } from '../../Context/MessageContext';

const DEFAULT_SEARCH_OPTIONS = {
    productName: '',
    productId: null,
};

const ReviewTransfer = ({ service, resource, entity: reviewEntity }) => {
    const [searchOptions, setSearchOptions] = useState(DEFAULT_SEARCH_OPTIONS);
    const [availableProducts, setAvailableProducts] = useState([]);
    const { pushMessage } = useContext(MessageContext);
    const { t } = useTranslation();

    // Selection
    const [marked, setMarked] = useState(null);
    const [selectError, setSelectError] = useState(false);
    const markEntityForTransfer = (entity) => setMarked(entity);
    const clearMark = () => {
        setMarked(null);
        setSelectError(false);
    };

    const refetchProducts = useDebounce(500, () => {
        if (searchOptions.productId !== null) {
            service
                .find('products', searchOptions.productId)
                .then((data) => {
                    setAvailableProducts([data.data]);
                })
                .catch(() => {
                    setAvailableProducts([]);
                });
        } else {
            service
                .get('products', {
                    name: searchOptions.productName,
                    per_page: 10,
                })
                .then((data) => {
                    setAvailableProducts(data.data);
                })
                .catch(() => {
                    setAvailableProducts([]);
                });
        }
    });

    const handleInputChange = (e) => {
        setSearchOptions(() => ({
            ...DEFAULT_SEARCH_OPTIONS,
            [e.target.name]: e.target.value,
        }));
    };

    useEffect(() => {
        if (
            searchOptions.productName.length > 0 ||
            searchOptions.productId !== null
        ) {
            refetchProducts();
        }
    }, [searchOptions.productId, searchOptions.productName]);

    const transferReview = () => {
        service
            .post('reviews', 'transfer', {
                reviews: [reviewEntity.id],
                to: marked.id,
            })
            .then(() => {
                clearMark();
                pushMessage({
                    title: t('application.transfer_success'),
                });
            })
            .catch(() => {
                setSelectError('transfer_error');
                pushMessage({
                    title: t('application.transfer_error'),
                    type: 'error',
                });
            });
    };

    return (
        <div className="w-full bg-light-snow text-2xl">
            <div className="flex gap-8 items-center px-8 pt-8 pb-4">
                <div className="flex flex-col w-full">
                    <label htmlFor="productName">
                        {t(`${resource}.product_name_label`)}
                    </label>
                    <input
                        id="productName"
                        name="productName"
                        className={style.formTextInput}
                        type="text"
                        onChange={handleInputChange}
                        value={searchOptions.productName}
                        autoComplete="off"
                        placeholder={t(`${resource}.product_name_placeholder`)}
                    />
                </div>

                <TextResource name="transfer_or_label" resource={resource} />

                <div className="flex flex-col w-full">
                    <label htmlFor="productId">
                        {t(`${resource}.product_id_label`)}
                    </label>
                    <input
                        id="productId"
                        name="productId"
                        className={style.formTextInput}
                        type="number"
                        min="0"
                        onChange={handleInputChange}
                        value={searchOptions.productId ?? ''}
                        autoComplete="off"
                        placeholder={t(`${resource}.product_id_placeholder`)}
                    />
                </div>
            </div>

            <Sublist
                service={service}
                resource="products"
                entity={availableProducts}
                overrideDelete
                handleDelete={markEntityForTransfer}
            >
                <Text name="id" />
                <Text name="name" />
                <Select name="select" />
            </Sublist>

            <Modal
                contentLabel={t(`application.confirm_selection`)}
                isOpen={!!marked}
                onRequestClose={clearMark}
            >
                {marked && (
                    <p>
                        {t('application.transfer_description', {
                            product: marked.name || `ID ${marked.id}`,
                        })}
                    </p>
                )}
                {!selectError && (
                    <Button
                        name="transfer_confirm_button"
                        click={transferReview}
                    />
                )}
                {selectError && (
                    <div className={styleList.errorMessage}>
                        {t(`application.${selectError}`, {
                            resource: t(`application.${resource}`),
                        })}
                    </div>
                )}
            </Modal>
        </div>
    );
};

export default ReviewTransfer;

ReviewTransfer.propTypes = {
    /**
     * Type of service
     */
    service: PropTypes.object.isRequired,
    /**
     * Type of resource
     */
    resource: PropTypes.string.isRequired,
    /**
     * Type of entity
     */
    entity: PropTypes.object.isRequired,
};
